import {MediaHelperService} from "../../../core/media-helper.service";

/** The backend maps 'medium' to 600x600 px, enough for a printed report image. */
export const REPORT_IMAGE_SIZE = 'medium';

/** Ids of the first image of each object, which is the one most reports show. */
export function thumbnailImageIds(artifacts: any[]): string[] {
  const imageIds = (artifacts ?? [])
    .map(artifact => artifact.thumbnailImage?.image_id)
    .filter(imageId => !!imageId);
  return [...new Set<string>(imageIds)];
}

/** Ids of every image of every object, for reports that show all images. */
export function allImageIds(artifacts: any[]): string[] {
  const imageIds = (artifacts ?? [])
    .flatMap(artifact => artifact.images ?? [])
    .map(image => image.image_id)
    .filter(imageId => !!imageId);
  return [...new Set<string>(imageIds)];
}

/**
 * Fetches the URLs of all report images in one request, instead of one
 * request per image. Images without a URL are left out of the map.
 */
export async function fetchReportImageUrls(
    mediaHelper: MediaHelperService,
    imageIds: string[],
    size: string = REPORT_IMAGE_SIZE): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  if (imageIds.length === 0) {
    return urls;
  }
  const res = await mediaHelper.getImageUrlsFromImageIds(imageIds, [size]);
  imageIds.forEach(imageId => {
    const url = res?.[imageId]?.[size];
    if (url) {
      urls.set(imageId, url);
    }
  });
  return urls;
}

/**
 * Resolves once every image under `root` has loaded or failed. The PDF
 * generator prints the page when the report is done, so a report must not
 * say it is done while its images are still loading.
 */
export async function waitForImages(root: HTMLElement): Promise<void> {
  await waitForThumbnails(root);
  const images = Array.from(root.querySelectorAll('img'));
  return Promise.all(images.map(image => {
    if (image.complete) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      image.addEventListener('load', () => resolve(), {once: true});
      image.addEventListener('error', () => resolve(), {once: true});
    });
  })).then(() => undefined);
}

/** Stop waiting for thumbnails after this long, so a stuck one cannot block the report. */
const THUMBNAIL_WAIT_LIMIT_MS = 30_000;
const THUMBNAIL_POLL_MS = 100;

/**
 * Resolves once every app-thumbnail under `root` shows its image or its
 * broken-image icon. A thumbnail adds its <img> only after it has set its
 * URL, so the images cannot be waited for before that.
 */
function waitForThumbnails(root: HTMLElement): Promise<void> {
  const started = Date.now();
  return new Promise<void>(resolve => {
    const check = () => {
      const thumbnails = Array.from(root.querySelectorAll('app-thumbnail'));
      const pending = thumbnails.filter(thumbnail => !thumbnail.querySelector('img, mat-icon'));
      if (pending.length === 0 || Date.now() - started > THUMBNAIL_WAIT_LIMIT_MS) {
        resolve();
      } else {
        setTimeout(check, THUMBNAIL_POLL_MS);
      }
    };
    check();
  });
}

/**
 * Gives each object's first image the URL of its thumbnail, so app-thumbnail
 * shows it without asking the backend once per object.
 */
export function setThumbnailUrls(artifacts: any[], urls: Map<string, string>) {
  (artifacts ?? []).forEach(artifact => {
    const url = artifact.thumbnailImage && urls.get(artifact.thumbnailImage.image_id);
    if (url) {
      artifact.thumbnailImage.thumbnail_url = url;
    }
  });
}
